import React from 'react';

type Align = 'left' | 'center' | 'right';

type Column<T> = {
  key: string;
  header: React.ReactNode;
  align?: Align;
  headerClassName?: string;
  cellClassName?: string;
  render: (row: T, index: number) => React.ReactNode;
};

type BeautifulTableProps<T> = {
  rows: T[];
  columns: Column<T>[];
  getRowKey: (row: T, index: number) => React.Key;
  emptyText?: string;
  containerClassName?: string;
};

const cn = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(' ');

export function BeautifulTable<T>({
  rows,
  columns,
  getRowKey,
  emptyText = '尚無資料',
  containerClassName = '',
}: BeautifulTableProps<T>) {
  return (
    <div
      className={cn(
        'overflow-hidden rounded-xl shadow-md border border-gray-100',
        containerClassName
      )}
    >
      <table className="min-w-full table-auto">
        <thead>
        <tr className="bg-gradient-to-r from-gray-50 to-gray-100 text-sm font-semibold text-gray-700">
          {columns.map((col) => (
            <th
              key={col.key}
              className={cn(
                'px-4 py-3',
                col.align === 'right' && 'text-right',
                col.align === 'center' && 'text-center',
                col.headerClassName
              )}
            >
              {col.header}
            </th>
          ))}
        </tr>
        </thead>

        <tbody className="divide-y divide-gray-100">
        {rows.length === 0 ? (
          <tr>
            <td
              colSpan={columns.length}
              className="px-4 py-10 text-center text-sm text-gray-400"
            >
              {emptyText}
            </td>
          </tr>
        ) : (
          rows.map((row, index) => (
            <tr key={getRowKey(row, index)} className="transition hover:bg-gray-50">
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={cn(
                    'px-4 py-3',
                    col.align === 'right' && 'text-right',
                    col.align === 'center' && 'text-center',
                    col.cellClassName
                  )}
                >
                  {col.render(row, index)}
                </td>
              ))}
            </tr>
          ))
        )}
        </tbody>
      </table>
    </div>
  );
}